import { c as _c } from "react/compiler-runtime";
import type { VaultValue } from '@onefootprint/core';
import { IcoUsers16 } from '@onefootprint/icons';
import type { DataIdentifier, Entity, SameTenantDupe } from '@onefootprint/request-types/dashboard';
import { Divider, IconButton, Tooltip } from '@onefootprint/ui';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useEntityDuplicateData from '@/components/entities/components/details/hooks/use-entity-duplicate-data';
import { getCardDis } from '@/components/entities/utils/get-dis';
import FieldOrPlaceholder from '@/components/field-or-placeholder';
import useAutoDecryptControls from '@/entity/hooks/use-auto-decrypt-controls';
import type { EntityCard } from '@/types/vault';
import getCards from '../../../../../../utils/get-cards';
import type { DiField } from '../../../../../../vault.types';
import Field from '../../../../../field';
import useGetCardIssuer from '../../utils/use-get-card-issuer';
import useGetTranslationWithoutAlias from '../../utils/use-get-translation-without-alias';
import CardDuplicateDrawer from './components/card-duplicate-drawer';
import CardSelector from './components/card-selector';
export type CardFieldsProps = {
  entity: Entity;
  setSelectedItemDis: (dis: DataIdentifier[]) => void;
};
const CardFields = t0 => {
  const $ = _c(40);
  const {
    entity,
    setSelectedItemDis
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "duplicateData.drawer.card"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("entity-details", t1);
  let t2;
  if ($[1] !== entity) {
    t2 = getCards(entity);
    $[1] = entity;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const cards = t2;
  const [selectedCard, setSelectedCard] = useState(cards.length > 0 ? cards[0] : undefined);
  const [duplicateDrawerOpen, setDuplicateDrawerOpen] = useState(false);
  const autoDecryptControls = useAutoDecryptControls(entity.kind);
  const getCardIssuer = useGetCardIssuer();
  const getTranslationsWithoutAlias = useGetTranslationWithoutAlias();
  const {
    data: duplicateData,
    isLoading: isDuplicateDataLoading
  } = useEntityDuplicateData(entity.id);
  let t3;
  if ($[3] !== duplicateData?.data || $[4] !== selectedCard?.fingerprint) {
    t3 = duplicateData?.data.filter(dupe => dupe.data.some(data => data.value === selectedCard?.fingerprint));
    $[3] = duplicateData?.data;
    $[4] = selectedCard?.fingerprint;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const filteredDupes = t3;
  const hasDupes = !!filteredDupes?.length;
  const dis = getCardDis(entity.data, selectedCard?.alias);
  const fields = dis.map(_temp);
  let t4;
  if ($[6] !== selectedCard) {
    t4 = [selectedCard];
    $[6] = selectedCard;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  useEffect(() => {
    setSelectedItemDis(dis);
  }, t4);
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      setDuplicateDrawerOpen(true);
    };
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const openDuplicateDataDrawer = t5;
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      setDuplicateDrawerOpen(false);
    };
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const closeDuplicateDataDrawer = t6;
  let t7;
  if ($[10] !== hasDupes || $[11] !== t) {
    t7 = value => {
      if (typeof value === "string") {
        return <div className="flex gap-1 items-center"><FieldOrPlaceholder data={value} />{hasDupes && <Tooltip content={t(_temp2)}><IconButton size="compact" aria-label={t(_temp3)} icon={IcoUsers16} onClick={openDuplicateDataDrawer} /></Tooltip>}</div>;
      }
      return null;
    };
    $[10] = hasDupes;
    $[11] = t;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const renderCardFingerprint = t7;
  let t8;
  if ($[13] !== autoDecryptControls || $[14] !== entity.id || $[15] !== getCardIssuer) {
    t8 = value_0 => {
      const changedValue = getCardIssuer(value_0);
      return <FieldOrPlaceholder data={changedValue} isLoading={autoDecryptControls.isEntityAutoDecrypting(entity.id)} />;
    };
    $[13] = autoDecryptControls;
    $[14] = entity.id;
    $[15] = getCardIssuer;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const renderCardIssuer = t8;
  let t9;
  if ($[17] !== entity || $[18] !== getTranslationsWithoutAlias || $[19] !== renderCardFingerprint || $[20] !== renderCardIssuer) {
    t9 = field => {
      const {
        di: di_0
      } = field;
      if (di_0.endsWith("fingerprint")) {
        return <Field renderValue={renderCardFingerprint} key={di_0} di={di_0} entity={entity} renderLabel={() => getTranslationsWithoutAlias(di_0)} />;
      }
      if (di_0.endsWith("issuer")) {
        return <Field renderValue={renderCardIssuer} key={di_0} di={di_0} entity={entity} renderLabel={() => getTranslationsWithoutAlias(di_0)} />;
      }
      return <Field key={di_0} di={di_0} entity={entity} renderLabel={() => getTranslationsWithoutAlias(di_0)} />;
    };
    $[17] = entity;
    $[18] = getTranslationsWithoutAlias;
    $[19] = renderCardFingerprint;
    $[20] = renderCardIssuer;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  const renderField = t9;
  let t10;
  if ($[22] !== cards || $[23] !== selectedCard) {
    t10 = cards?.length > 1 && <><CardSelector cards={cards} selected={selectedCard} onChange={setSelectedCard} /><Divider /></>;
    $[22] = cards;
    $[23] = selectedCard;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  const t11 = "flex flex-col gap-3";
  let t12;
  if ($[25] !== renderField) {
    t12 = field_0 => renderField(field_0);
    $[25] = renderField;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  const t13 = fields.map(t12);
  let t14;
  if ($[27] !== t13) {
    t14 = <div className={t11}>{t13}</div>;
    $[27] = t13;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] !== filteredDupes) {
    t15 = filteredDupes ?? [];
    $[29] = filteredDupes;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  let t16;
  if ($[31] !== closeDuplicateDataDrawer || $[32] !== duplicateDrawerOpen || $[33] !== isDuplicateDataLoading || $[34] !== t15) {
    t16 = <CardDuplicateDrawer isOpen={duplicateDrawerOpen} onClose={closeDuplicateDataDrawer} dupes={t15} isLoading={isDuplicateDataLoading} />;
    $[31] = closeDuplicateDataDrawer;
    $[32] = duplicateDrawerOpen;
    $[33] = isDuplicateDataLoading;
    $[34] = t15;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  let t17;
  if ($[36] !== t10 || $[37] !== t14 || $[38] !== t16) {
    t17 = <>{t10}{t14}{t16}</>;
    $[36] = t10;
    $[37] = t14;
    $[38] = t16;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  return t17;
};
export default CardFields;
function _temp(di) {
  return {
    di
  };
}
function _temp2($) {
  return $.cta;
}
function _temp3($_0) {
  return $_0.cta;
}